// Google Maps API Key
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';

/**
 * Strip the administrative prefix from a Romanian commune/city name so that
 * "Comuna Mircești", "Mircești" and "Mircesti" (no diacritics) all resolve
 * to the same canonical display name.
 *
 * Prefixes handled: Comuna, Comună, Oraș, Oras, Municipiul, Municipiu, Sat, Sector
 * Also normalises Unicode (NFD) diacritics so the comparison helpers work correctly.
 *
 * @param {string} s  Raw name from the geocoding API or stored data
 * @returns {string}  Clean display name, e.g. "Mircești"
 */
export const cleanComunaName = (s) => {
  if (!s) return '';
  return s
    .replace(/^(Municipiul?|Oraș|Oras|Comună|Comuna|Sat|Sector)\s+/i, '')
    .trim();
};

// Villages and spellings that belong to one comună.
const COMUNA_ALIASES = { iugani: 'Mircești', mircesti: 'Mircești' };

const VILLAGES = ['iugani'];

// A village can be someone's address, but it isn't a comună for land.
export const isVillageName = (s) => VILLAGES.includes(
  cleanComunaName(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
);

/**
 * The name a comună is shown and totalled under: without prefix, with villages
 * counted as their comună (Iugani is a village in Mircești).
 */
export const canonicalComuna = (s) => {
  const clean = cleanComunaName(s);
  const key = clean.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  return COMUNA_ALIASES[key] || clean;
};

/**
 * Reverse geocode a lat/lng to get the Romanian Comună (administrative_area_level_2).
 *
 * WHY TWO REQUESTS:
 *   First request adds result_type=administrative_area_level_2 — this asks Google to look
 *   up which admin-boundary polygon actually contains the coordinates, which is far more
 *   accurate than deriving the commune from a road/address result (the old approach).
 *   If that returns no data (rural areas with incomplete boundary data), we fall back to
 *   the unfiltered request and pick the first result that carries the component.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string>} The comune name, or '' if not found
 */
export const reverseGeocodeComuna = async (lat, lng) => {
  const apiKey = GOOGLE_MAPS_API_KEY;
  if (!apiKey) return '';
  try {
    // ── Primary: query the administrative boundary layer directly ──────────────
    const primaryUrl =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?latlng=${lat},${lng}` +
      `&result_type=administrative_area_level_2` +
      `&key=${apiKey}` +
      `&language=ro`;
    const primaryRes  = await fetch(primaryUrl);
    const primaryData = await primaryRes.json();
    if (primaryData.status === 'REQUEST_DENIED') {
      console.error('Comună detection refused by Google:', primaryData.error_message);
      return '';
    }

    if (primaryData.status === 'OK' && primaryData.results.length > 0) {
      const comp = primaryData.results[0].address_components.find(
        c => c.types.includes('administrative_area_level_2')
      );
      if (comp) return cleanComunaName(comp.long_name);
    }

    // ── Fallback: unfiltered search (original behaviour) ──────────────────────
    const fallbackUrl =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?latlng=${lat},${lng}` +
      `&key=${apiKey}` +
      `&language=ro`;
    const fallbackRes  = await fetch(fallbackUrl);
    const fallbackData = await fallbackRes.json();

    if (fallbackData.status === 'OK' && fallbackData.results.length > 0) {
      // Look for the result whose type IS administrative_area_level_2 first
      for (const result of fallbackData.results) {
        if (result.types.includes('administrative_area_level_2')) {
          const comp = result.address_components.find(
            c => c.types.includes('administrative_area_level_2')
          );
          if (comp) return cleanComunaName(comp.long_name);
        }
      }
      // Last resort: any result that carries it as an address component
      for (const result of fallbackData.results) {
        const comp = result.address_components.find(
          c => c.types.includes('administrative_area_level_2')
        );
        if (comp) return cleanComunaName(comp.long_name);
      }
    }
  } catch (err) {
    console.error('Reverse geocoding failed:', err);
  }
  return '';
};

/**
 * Calculate the area of a polygon in hectares
 * @param {Array} coordinates - Array of {lat, lng} coordinates
 * @returns {number} Area in hectares
 */
export const calculatePolygonArea = (coordinates) => {
  if (!coordinates || coordinates.length < 3) {
    return 0;
  }

  // Use the Earth radius in meters
  const earthRadiusInMeters = 6371000;

  // Convert polygon coordinates to radians
  const coordinatesInRadians = coordinates.map(coord => ({
    lat: (coord.lat * Math.PI) / 180,
    lng: (coord.lng * Math.PI) / 180
  }));

  let area = 0;
  
  // Calculate area using the spherical polygon formula
  for (let i = 0; i < coordinatesInRadians.length; i++) {
    const j = (i + 1) % coordinatesInRadians.length;
    
    const xi = coordinatesInRadians[i].lng;
    const yi = coordinatesInRadians[i].lat;
    const xj = coordinatesInRadians[j].lng;
    const yj = coordinatesInRadians[j].lat;
    
    area += xi * Math.sin(yj) - xj * Math.sin(yi);
  }
  
  area = Math.abs(area * earthRadiusInMeters * earthRadiusInMeters / 2);
  
  // Convert square meters to hectares (1 hectare = 10,000 square meters)
  return area / 10000;
};

/**
 * Checks if a point is inside a polygon using the ray casting algorithm
 * @param {Object} point - Point with lat, lng properties
 * @param {Array} polygon - Array of {lat, lng} coordinates forming a polygon
 * @returns {boolean} True if the point is inside the polygon
 */
export const pointInPolygon = (point, polygon) => {
  if (!point || !polygon || polygon.length < 3) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    
    const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
      (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
    
    if (intersect) inside = !inside;
  }
  
  return inside;
};

/**
 * Parse a KML file and extract polygon data
 * @param {string} kmlString - KML file contents as string
 * @returns {Array} Array of polygon objects with coordinates
 */
export const parseKmlFile = (kmlString) => {
  const parser = new DOMParser();
  const kml = parser.parseFromString(kmlString, 'text/xml');
  
  // Check if the document is valid KML
  if (kml.documentElement.nodeName === 'parsererror') {
    throw new Error('Invalid KML file');
  }
  
  const polygons = [];
  const placemarks = kml.getElementsByTagName('Placemark');
  
  // Loop through each Placemark
  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];
    const name = placemark.getElementsByTagName('name')[0]?.textContent || `Imported Field ${i + 1}`;
    const description = placemark.getElementsByTagName('description')[0]?.textContent || '';
    
    // Look for polygons in the placemark
    const polygonElements = placemark.getElementsByTagName('Polygon');
    
    if (polygonElements.length > 0) {
      // Process each polygon
      for (let j = 0; j < polygonElements.length; j++) {
        const polygon = polygonElements[j];
        const coordinates = [];
        
        // Get coordinates
        const coordinatesElements = polygon.getElementsByTagName('coordinates');
        if (coordinatesElements.length > 0) {
          const coordinatesText = coordinatesElements[0].textContent.trim();
          const coordinateStrings = coordinatesText.split(/\s+/);
          
          // Parse each coordinate triplet (lon,lat,alt)
          coordinateStrings.forEach(coordString => {
            if (coordString) {
              const [lng, lat, _] = coordString.split(',').map(parseFloat);
              if (!isNaN(lat) && !isNaN(lng)) {
                coordinates.push({ lat, lng });
              }
            }
          });
        }
        
        // Only add if we have a valid polygon (at least 3 points)
        if (coordinates.length >= 3) {
          const area = calculatePolygonArea(coordinates);
          polygons.push({
            id: `imported-${Date.now()}-${i}-${j}`,
            name,
            description,
            coordinates,
            area: area.toFixed(2),
            imported: true
          });
        }
      }
    }
  }
  
  return polygons;
};

/**
 * Handle file drop for KML import
 * @param {Event} event - Drop event
 * @returns {Promise<Array>} Parsed polygons
 */
export const handleFileDrop = async (event) => {
  event.preventDefault();
  event.stopPropagation();
  
  if (!event.dataTransfer.files || event.dataTransfer.files.length === 0) {
    return [];
  }
  
  const file = event.dataTransfer.files[0];
  
  // Check if it's a KML file
  if (!file.name.toLowerCase().endsWith('.kml')) {
    throw new Error('Please drop a KML file');
  }
  
  const fileContent = await file.text();
  return parseKmlFile(fileContent);
};
